"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ThemeContext,
  type Density,
  type ResolvedTheme,
  type Theme,
} from "./useTheme";

const THEME_KEY = "aio:theme";
const DENSITY_KEY = "aio:density";

function readSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyAttributes(resolved: ResolvedTheme, density: Density) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
  document.documentElement.setAttribute("data-density", density);
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultDensity?: Density;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultDensity = "comfortable",
}: ThemeProviderProps) {
  // Initial state must NOT read localStorage — that would produce a different
  // tree on the client than the server rendered, and any UI that branches on
  // `theme` (theme switcher buttons, etc.) would log a hydration mismatch.
  // Instead we render with the defaults on first paint, then sync from
  // localStorage in a layout effect. The blocking inline script in
  // `app/layout.tsx` already applied data-theme/data-density to <html> so
  // there's no color flash; only the React tree's `theme` state catches up.
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [density, setDensityState] = useState<Density>(defaultDensity);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>("light");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedTheme = localStorage.getItem(THEME_KEY) as Theme | null;
    if (storedTheme && storedTheme !== theme) setThemeState(storedTheme);
    const storedDensity = localStorage.getItem(DENSITY_KEY) as Density | null;
    if (storedDensity && storedDensity !== density) setDensityState(storedDensity);
    setSystemTheme(readSystemTheme());
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
    // Intentionally only run once on mount; we are syncing the bootstrap
    // state, not reacting to subsequent theme/density changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    applyAttributes(resolvedTheme, density);
  }, [resolvedTheme, density]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    localStorage.setItem(THEME_KEY, next);
  }, []);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    localStorage.setItem(DENSITY_KEY, next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, density, setDensity }),
    [theme, resolvedTheme, setTheme, density, setDensity],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
